import * as THREE from "three";

export type BattleFxKind = "ballistic" | "blade" | "beam" | "pulse" | "acid" | "claw" | "rocket" | "heal" | "shield" | "buff" | "debuff" | "ultimate";
export interface BattleFxEvent { kind: BattleFxKind; from: THREE.Vector3; to: THREE.Vector3; color?: number }
export const BATTLE_FX_LIMIT = 24;
type Mesh = THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
type Palette = { body: THREE.MeshBasicMaterial; core: THREE.MeshBasicMaterial; glow: THREE.MeshBasicMaterial };
interface Effect { event: BattleFxEvent; group: THREE.Group; palette: Palette; age: number; duration: number; animate: ((progress: number) => void)[] }

const COLORS: Record<BattleFxKind, number> = { ballistic: 0xe9b46a, blade: 0x9fd8e3, beam: 0x48bacd, pulse: 0xaaa0e9, acid: 0xaac45e, claw: 0xd78d7c, rocket: 0xeaa16a, heal: 0x81d8a1, shield: 0x69b9db, buff: 0xe0c275, debuff: 0xac80ba, ultimate: 0xf1cb75 };
const DURATION: Record<BattleFxKind, number> = { ballistic: 0.48, blade: 0.44, beam: 0.38, pulse: 0.66, acid: 0.84, claw: 0.50, rocket: 0.78, heal: 1.05, shield: 0.95, buff: 0.9, debuff: 0.9, ultimate: 1.25 };
const UP = new THREE.Vector3(0, 1, 0), ZERO = new THREE.Vector3(), direction = new THREE.Vector3();
const clamp = THREE.MathUtils.clamp;

function extrude(points: [number, number][]): THREE.ExtrudeGeometry {
  const shape = new THREE.Shape(points.map(([x, y]) => new THREE.Vector2(x, y)));
  shape.closePath();
  return new THREE.ExtrudeGeometry(shape, { depth: 0.065, bevelEnabled: false, steps: 1, curveSegments: 1 }).translate(0, 0, -0.0325);
}

/** Tapered curved blade, built as real geometry rather than a transparent sprite. */
function crescent(): THREE.BufferGeometry {
  const vertices: number[] = [], indices: number[] = [];
  for (let step = 0; step <= 24; step++) {
    const t = step / 24, angle = -1.05 + t * 2.1, width = Math.sin(Math.PI * t) * 0.22;
    for (const radius of [1 - width, 1 + width * 0.4]) vertices.push(Math.cos(angle) * radius, Math.sin(angle) * radius, 0);
    if (step < 24) { const a = step * 2; indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
  geometry.setIndex(indices); geometry.computeVertexNormals();
  return geometry;
}

/** Fits a shared unit cylinder between two points without changing either input vector. */
function segment(mesh: Mesh, a: THREE.Vector3, b: THREE.Vector3, radius: number): void {
  direction.subVectors(b, a);
  const length = direction.length();
  mesh.visible = length > 0.0001;
  mesh.position.copy(a).add(b).multiplyScalar(0.5);
  mesh.scale.set(radius, Math.max(length, 0.0001), radius);
  if (length > 0.0001) mesh.quaternion.setFromUnitVectors(UP, direction.divideScalar(length));
}

/**
 * Short replay cues with shared local geometry and three fading materials per active effect.
 * Coordinates are local to the parent arena. At most 24 cues and one ultimate are retained.
 */
export class BattleFx {
  readonly group = new THREE.Group();
  private readonly effects: Effect[] = [];
  private readonly geometry = {
    sphere: new THREE.IcosahedronGeometry(1, 1),
    crystal: new THREE.OctahedronGeometry(1, 0),
    cylinder: new THREE.CylinderGeometry(1, 1, 1, 8),
    cone: new THREE.ConeGeometry(1, 1, 8),
    ring: new THREE.TorusGeometry(1, 0.04, 5, 40),
    arc: crescent(),
    dome: new THREE.SphereGeometry(1, 16, 8, 0, Math.PI * 2, 0, Math.PI * 0.55),
    plus: extrude([[-0.12, 0.4], [0.12, 0.4], [0.12, 0.12], [0.4, 0.12], [0.4, -0.12], [0.12, -0.12], [0.12, -0.4], [-0.12, -0.4], [-0.12, -0.12], [-0.4, -0.12], [-0.4, 0.12], [-0.12, 0.12]]),
    shield: extrude([[-0.4, 0.4], [0.4, 0.4], [0.35, -0.12], [0, -0.45], [-0.35, -0.12]]),
    chevron: extrude([[-0.4, 0], [0, 0.34], [0.4, 0], [0.27, -0.12], [0, 0.11], [-0.27, -0.12]]),
    star: extrude(Array.from({ length: 10 }, (_, i) => { const a = Math.PI / 2 + i * Math.PI / 5, r = i % 2 ? 0.19 : 0.48; return [Math.cos(a) * r, Math.sin(a) * r] as [number, number]; })),
  };
  private reducedMotion: boolean;
  private disposed = false;

  constructor(options: { reducedMotion?: boolean } = {}) {
    this.reducedMotion = options.reducedMotion ?? false;
    this.group.name = "battle-fx";
    for (const [name, geometry] of Object.entries(this.geometry)) {
      geometry.name = "battle-fx-" + name;
      geometry.computeBoundingBox(); geometry.computeBoundingSphere();
    }
  }

  get activeCount(): number { return this.effects.length; }

  emit(input: BattleFxEvent): void {
    if (this.disposed || ![...input.from.toArray(), ...input.to.toArray()].every(Number.isFinite)) return;
    if (input.kind === "ultimate") for (const effect of [...this.effects]) if (effect.event.kind === "ultimate") this.remove(effect);
    while (this.effects.length >= BATTLE_FX_LIMIT) this.remove(this.effects[0]!);
    const color = input.color ?? COLORS[input.kind];
    const material = (tint: number, opacity: number, additive = false) => new THREE.MeshBasicMaterial({ color: tint, transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide, forceSinglePass: true, toneMapped: false, blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending });
    const effect: Effect = {
      event: { ...input, from: input.from.clone(), to: input.to.clone() }, group: new THREE.Group(),
      palette: { body: material(color, 0.82), core: material(0xfff1d9, 0.95), glow: material(color, 0.22, true) },
      age: 0, duration: this.reducedMotion ? 0.72 : DURATION[input.kind], animate: [],
    };
    effect.group.name = "battle-fx-" + input.kind;
    effect.group.userData = { kind: input.kind, reducedMotion: this.reducedMotion };
    effect.group.position.copy(effect.event.to);
    if (this.reducedMotion) this.quiet(effect); else this.build(effect);
    this.effects.push(effect); this.group.add(effect.group); this.pose(effect);
  }

  private mesh(effect: Effect, shape: keyof BattleFx["geometry"], part: string, material: keyof Palette = "body"): Mesh {
    const mesh = new THREE.Mesh(this.geometry[shape], effect.palette[material]);
    mesh.name = part; mesh.userData.part = part;
    mesh.castShadow = false; mesh.receiveShadow = false;
    effect.group.add(mesh); return mesh;
  }

  private ring(effect: Effect, part: string, radius: number, height = 0, material: keyof Palette = "body"): Mesh {
    const ring = this.mesh(effect, "ring", part, material);
    ring.rotation.x = Math.PI / 2; ring.scale.setScalar(radius); ring.position.y = height; return ring;
  }

  private impact(effect: Effect, at: number, radius: number, sparks = 3): void {
    const ring = this.ring(effect, "impact-shock-ring", radius, 0.02);
    const fragments = Array.from({ length: sparks }, (_, i) => ({ mesh: this.mesh(effect, "crystal", "impact-fragment-" + i, "core"), angle: i * Math.PI * 2 / sparks + 0.4 }));
    effect.animate.push((t) => {
      const p = clamp((t - at) / (1 - at), 0, 1);
      ring.visible = t >= at; ring.scale.setScalar(radius * (0.22 + p));
      for (const { mesh, angle } of fragments) {
        mesh.visible = t >= at;
        mesh.position.set(Math.cos(angle) * radius * p, 0.12 + Math.sin(p * Math.PI) * 0.25, Math.sin(angle) * radius * p);
        mesh.scale.set(0.028 * (1 - p), 0.15 * (1 - p), 0.028 * (1 - p)); mesh.rotation.z = -angle;
      }
    });
  }

  private projectile(effect: Effect, rocket: boolean): void {
    const from = effect.event.from.clone().sub(effect.event.to), point = new THREE.Vector3(), behind = new THREE.Vector3();
    const body = this.mesh(effect, rocket ? "cylinder" : "crystal", rocket ? "rocket-body" : "ballistic-round", "core");
    const trail = this.mesh(effect, "cylinder", rocket ? "rocket-exhaust-trail" : "ballistic-tracer");
    const nose = rocket ? this.mesh(effect, "cone", "rocket-warhead") : null;
    const plume = rocket ? this.mesh(effect, "cone", "rocket-exhaust", "glow") : null;
    const muzzle = rocket ? null : this.ring(effect, "muzzle-iris", 0.13, 0, "core");
    const path = (out: THREE.Vector3, p: number) => { out.lerpVectors(from, ZERO, p); out.y += Math.sin(p * Math.PI) * (rocket ? 0.7 : 0.035); };
    effect.animate.push((t) => {
      const p = clamp(t / 0.58, 0, 1); path(point, p); path(behind, Math.max(0, p - (rocket ? 0.13 : 0.10)));
      body.visible = t < 0.58; body.position.copy(point);
      body.scale.set(rocket ? 0.065 : 0.04, rocket ? 0.28 : 0.14, rocket ? 0.065 : 0.04);
      direction.subVectors(point, behind); if (direction.lengthSq() > 0.00001) body.quaternion.setFromUnitVectors(UP, direction.normalize());
      segment(trail, behind, point, rocket ? 0.027 : 0.013); trail.visible &&= t < 0.58;
      if (nose && plume) {
        nose.visible = plume.visible = body.visible; nose.quaternion.copy(body.quaternion); plume.quaternion.copy(body.quaternion);
        nose.position.copy(point).addScaledVector(direction, 0.2); nose.scale.set(0.08, 0.17, 0.08);
        plume.position.copy(point).addScaledVector(direction, -0.23); plume.scale.set(0.09, -0.32, 0.09);
      }
      if (muzzle) { muzzle.position.copy(from); muzzle.visible = t < 0.25; muzzle.scale.setScalar(0.08 + t * 0.4); }
    });
    this.impact(effect, 0.52, rocket ? 0.75 : 0.4, rocket ? 3 : 2);
  }

  private build(effect: Effect): void {
    const kind = effect.event.kind;
    if (kind === "ballistic" || kind === "rocket") { this.projectile(effect, kind === "rocket"); return; }
    if (kind === "blade" || kind === "claw") {
      const count = kind === "claw" ? 3 : 2;
      for (let i = 0; i < count; i++) {
        const slash = this.mesh(effect, "arc", kind + "-slash-" + i, i === 1 && kind === "blade" ? "core" : "body");
        slash.position.set((i - (count - 1) / 2) * 0.19, 0.06, 0); slash.rotation.set(-0.4, kind === "blade" ? -0.7 : 0.15, -0.9);
        effect.animate.push((t) => { slash.rotation.z = -1.1 + t * (kind === "blade" ? 2.1 : 0.6); slash.scale.setScalar((kind === "blade" ? 0.82 - i * 0.1 : 0.57) * (0.7 + Math.sin(t * Math.PI) * 0.3)); });
      }
      this.impact(effect, 0.18, 0.3, 1); return;
    }
    if (kind === "beam") {
      const from = effect.event.from.clone().sub(effect.event.to);
      const outer = this.mesh(effect, "cylinder", "beam-envelope", "glow"), core = this.mesh(effect, "cylinder", "beam-core", "core");
      const muzzle = this.ring(effect, "beam-emitter", 0.14); muzzle.position.copy(from);
      segment(outer, from, ZERO, 0.08); segment(core, from, ZERO, 0.018);
      effect.animate.push((t) => { outer.scale.x = outer.scale.z = 0.08 * (1 - t * 0.55); core.scale.x = core.scale.z = 0.018 * (1 - t * 0.5); });
      this.impact(effect, 0, 0.35, 1); return;
    }
    if (kind === "pulse") {
      const from = effect.event.from.clone().sub(effect.event.to), axis = ZERO.clone().sub(from).normalize();
      for (let i = 0; i < 3; i++) {
        const packet = this.mesh(effect, "ring", "pulse-wavefront-" + i, i === 1 ? "core" : "body");
        if (axis.lengthSq() > 0) packet.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), axis);
        effect.animate.push((t) => { const p = clamp((t - i * 0.10) / 0.7, 0, 1); packet.position.lerpVectors(from, ZERO, p); packet.scale.setScalar(0.10 + p * 0.3); packet.visible = t >= i * 0.10; });
      }
      this.impact(effect, 0.6, 0.55, 1); return;
    }
    if (kind === "acid") {
      const from = effect.event.from.clone().sub(effect.event.to);
      for (let i = 0; i < 3; i++) {
        const glob = this.mesh(effect, "sphere", "acid-glob-" + i);
        effect.animate.push((t) => { const p = clamp((t - i * 0.055) / 0.52, 0, 1); glob.visible = p < 1; glob.position.lerpVectors(from, ZERO, p); glob.position.y += Math.sin(p * Math.PI) * (0.45 + i * 0.12); glob.scale.setScalar(0.105 - i * 0.022); });
      }
      const puddle = this.mesh(effect, "sphere", "acid-ground-splash", "glow"); puddle.position.y = -effect.event.to.y + 0.045;
      effect.animate.push((t) => { const p = clamp((t - 0.45) / 0.55, 0, 1); puddle.visible = t >= 0.45; puddle.scale.set(0.18 + p * 0.46, 0.018, 0.12 + p * 0.3); });
      this.impact(effect, 0.48, 0.4, 2); return;
    }
    if (kind === "heal") {
      const cross = this.mesh(effect, "plus", "heal-cross");
      effect.animate.push((t) => { cross.position.y = 0.25 + t * 0.55; cross.scale.setScalar(0.6 + Math.sin(Math.PI * t) * 0.09); });
      for (let i = 0; i < 2; i++) {
        const hoop = this.ring(effect, "heal-restoration-ring-" + i, 0.5, -0.3, i ? "glow" : "body");
        effect.animate.push((t) => { hoop.position.y = -0.3 + t * 0.9 + i * 0.12; hoop.scale.setScalar(0.45 + Math.sin(Math.PI * t) * 0.13); });
      }
      for (let i = 0; i < 3; i++) {
        const mote = this.mesh(effect, "crystal", "heal-mote-" + i, "core"); mote.scale.setScalar(0.04);
        effect.animate.push((t) => mote.position.set(Math.cos(i * 2.1) * 0.35, t * 0.9 - i * 0.12, Math.sin(i * 2.1) * 0.35));
      }
      return;
    }
    if (kind === "shield") {
      const dome = this.mesh(effect, "dome", "shield-canopy", "glow"); dome.position.y = -0.45;
      const belt = this.ring(effect, "shield-perimeter", 0.77, -0.4);
      const crest = this.mesh(effect, "shield", "shield-crest"); crest.position.set(0, 0.52, 0.18); crest.scale.setScalar(0.55);
      const inset = this.mesh(effect, "shield", "shield-crest-inset", "core"); inset.position.copy(crest.position).add(new THREE.Vector3(0, 0.02, 0.045)); inset.scale.setScalar(0.28);
      effect.animate.push((t) => { const s = 0.74 + Math.sin(Math.PI * Math.min(t * 1.7, 1)) * 0.06; dome.scale.set(s, s * 1.1, s); belt.scale.setScalar(s); }); return;
    }
    if (kind === "buff" || kind === "debuff") {
      const sign = kind === "buff" ? 1 : -1;
      for (let i = 0; i < 2; i++) {
        const arrow = this.mesh(effect, "chevron", kind + "-chevron-" + i, i ? "core" : "body");
        arrow.rotation.z = kind === "debuff" ? Math.PI : 0; arrow.scale.setScalar(0.62);
        effect.animate.push((t) => { arrow.position.y = 0.2 + i * 0.28 + sign * t * 0.24; });
      }
      const field = this.ring(effect, kind + "-field", 0.55, -0.3, "glow");
      effect.animate.push((t) => field.scale.setScalar(0.5 + Math.sin(Math.PI * t) * 0.15));
      if (kind === "debuff") { const seal = this.mesh(effect, "ring", "debuff-inhibition-seal"); seal.position.y = 0.3; seal.scale.setScalar(0.3); } return;
    }
    // Local ultimate crown: no screen-filling flash or camera disturbance.
    const core = this.mesh(effect, "crystal", "ultimate-focus", "core"), crown = this.mesh(effect, "star", "ultimate-crown"); crown.position.y = 1.25;
    for (let i = 0; i < 2; i++) {
      const ring = this.ring(effect, "ultimate-shockwave-" + i, 0.2, -effect.event.to.y + 0.06 + i * 0.07, i ? "glow" : "body");
      effect.animate.push((t) => ring.scale.setScalar(0.2 + t * (1.5 - i * 0.3)));
    }
    for (let i = 0; i < 3; i++) {
      const ray = this.mesh(effect, "arc", "ultimate-orbit-" + i, "glow"); ray.rotation.set(0.2, i * Math.PI / 3, 0.5);
      effect.animate.push((t) => { ray.scale.setScalar(0.7 + t * 0.35); ray.rotation.z = 0.5 + t * 1.2; ray.position.y = 0.15 + t * 0.35; });
    }
    effect.animate.push((t) => { core.position.y = 0.15 + t * 0.55; core.scale.set(0.18, 0.45 + Math.sin(Math.PI * t) * 0.2, 0.18); core.rotation.y = t * Math.PI; crown.scale.setScalar(0.55 + Math.sin(Math.PI * t) * 0.15); });
  }

  private quiet(effect: Effect): void {
    const kind = effect.event.kind;
    const rim = this.mesh(effect, "ring", "quiet-marker-rim", "glow"); rim.scale.setScalar(0.49); rim.position.y = 0.7;
    const icon = (shape: keyof BattleFx["geometry"], name: string, x = 0, y = 0, scale = 0.65) => {
      const mesh = this.mesh(effect, shape, name); mesh.position.set(x, 0.7 + y, 0.025); mesh.scale.setScalar(scale); return mesh;
    };
    if (kind === "heal") icon("plus", "quiet-heal-cross");
    else if (kind === "shield") icon("shield", "quiet-shield-crest");
    else if (kind === "ultimate") icon("star", "quiet-ultimate-star");
    else if (kind === "buff" || kind === "debuff") {
      for (let i = 0; i < 2; i++) icon("chevron", "quiet-" + kind + "-chevron-" + i, 0, (i - 0.5) * 0.2, 0.58).rotation.z = kind === "debuff" ? Math.PI : 0;
      if (kind === "debuff") icon("ring", "quiet-debuff-seal", 0, 0, 0.29);
    } else if (kind === "blade" || kind === "claw") {
      for (let i = 0; i < (kind === "claw" ? 3 : 1); i++) { const arc = icon("arc", "quiet-" + kind + "-slash-" + i, -0.2 + i * 0.12, 0, 0.3); arc.rotation.z = -0.6; }
    } else if (kind === "pulse") {
      for (let i = 0; i < 3; i++) icon("ring", "quiet-pulse-ring-" + i, 0, 0, 0.13 + i * 0.095);
    } else if (kind === "acid") {
      for (let i = 0; i < 3; i++) { const drop = icon("cone", "quiet-acid-drop-" + i, (i - 1) * 0.18, i === 1 ? 0.06 : -0.07, 1); drop.scale.set(0.07, 0.2, 0.045); }
    } else if (kind === "beam") {
      const beam = icon("cylinder", "quiet-beam-line", 0, 0, 1); beam.scale.set(0.025, 0.52, 0.025);
      for (const sign of [-1, 1]) icon("sphere", "quiet-beam-end-" + sign, 0, sign * 0.26, 0.06);
    } else {
      const body = icon(kind === "rocket" ? "cylinder" : "crystal", "quiet-" + kind + "-body", 0, -0.04, 1); body.scale.set(0.075, 0.34, 0.05);
      const tip = icon("cone", "quiet-" + kind + "-tip", 0, 0.20, 1); tip.scale.set(0.10, 0.16, 0.055);
      if (kind === "rocket") { const exhaust = icon("chevron", "quiet-rocket-exhaust", 0, -0.29, 0.32); exhaust.rotation.z = Math.PI; }
    }
  }

  private pose(effect: Effect): void {
    const progress = clamp(effect.age / effect.duration, 0, 1);
    for (const animate of effect.animate) animate(progress);
    const opacity = Math.min(1, (1 - progress) / 0.32);
    effect.palette.body.opacity = 0.82 * opacity; effect.palette.core.opacity = 0.95 * opacity; effect.palette.glow.opacity = 0.22 * opacity;
    effect.group.userData.progress = progress;
  }

  update(dt: number): void {
    if (this.disposed || !Number.isFinite(dt) || dt <= 0) return;
    for (let i = this.effects.length - 1; i >= 0; i--) {
      const effect = this.effects[i]!; effect.age += dt;
      if (effect.age >= effect.duration) this.remove(effect); else this.pose(effect);
    }
  }

  setReducedMotion(value: boolean): void {
    if (this.disposed || value === this.reducedMotion) return;
    this.reducedMotion = value;
    // Replace motion already in progress with the corresponding static marker immediately.
    if (value) { const events = this.effects.map((effect) => effect.event); this.clear(); for (const event of events) this.emit(event); }
  }

  private remove(effect: Effect): void {
    const index = this.effects.indexOf(effect); if (index < 0) return;
    this.effects.splice(index, 1); effect.group.removeFromParent();
    for (const material of Object.values(effect.palette)) material.dispose();
    effect.animate.length = 0; effect.group.clear();
  }

  clear(): void { for (const effect of [...this.effects]) this.remove(effect); }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true; this.clear(); this.group.removeFromParent();
    for (const geometry of Object.values(this.geometry)) geometry.dispose();
  }
}
